import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ReportCard from './components/ReportCard';
import ReportFilters from './components/ReportFilters';
import ReportPreview from './components/ReportPreview';
import CustomReportBuilder from './components/CustomReportBuilder';
import AnalyticsPanel from './components/AnalyticsPanel';
import { useUserData } from '../../hooks/useUserData';

const ReportsAnalytics = () => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('reports');
  const [selectedReport, setSelectedReport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showCustomBuilder, setShowCustomBuilder] = useState(false);
  const { userData } = useUserData();
  const projectionHorizon = userData?.setup?.projectionHorizon || 20;

  const [reports, setReports] = useState([
    {
      id: 'comprehensive-plan',
      title: 'Comprehensive Financial Plan',
      description: 'Complete overview of your financial health and retirement planning progress',
      icon: 'FileText',
      status: 'ready',
      lastGenerated: 'Dec 15, 2024',
      dateRange: 'Jan 2024 - Dec 2024',
      pages: 24
    },
    {
      id: 'asset-allocation',
      title: 'Asset Allocation Analysis',
      description: 'Detailed breakdown of your investment portfolio and asset distribution',
      icon: 'PieChart',
      status: 'ready',
      lastGenerated: 'Dec 10, 2024',
      dateRange: 'Current Holdings',
      pages: 12
    },
    {
      id: 'cash-flow',
      title: 'Cash Flow Projections',
      description: `${projectionHorizon}-year cash flow analysis with income, expenses, and net worth projections`,
      icon: 'TrendingUp',
      status: 'generating',
      lastGenerated: 'In Progress',
      dateRange: '2024 - 2049',
      pages: 18
    },
    {
      id: 'retirement-readiness',
      title: 'Retirement Readiness Assessment',
      description: 'Comprehensive evaluation of your retirement preparedness and gap analysis',
      icon: 'Target',
      status: 'ready',
      lastGenerated: 'Dec 8, 2024',
      dateRange: 'Current Status',
      pages: 16
    },
    {
      id: 'scenario-comparison',
      title: 'Scenario Comparison Report',
      description: 'Side-by-side analysis of different retirement planning scenarios',
      icon: 'GitBranch',
      status: 'draft',
      lastGenerated: 'Never',
      dateRange: 'Multiple Scenarios',
      pages: 22
    },
    {
      id: 'tax-optimization',
      title: 'Tax Optimization Strategy',
      description: 'Analysis of tax-efficient investment and withdrawal strategies',
      icon: 'Calculator',
      status: 'ready',
      lastGenerated: 'Dec 5, 2024',
      dateRange: 'Tax Year 2024',
      pages: 14
    }
  ]);

  // Keep the cash-flow report description in sync with projection horizon
  useEffect(() => {
    setReports(prev => prev.map(r => r.id === 'cash-flow'
      ? { ...r, description: `${projectionHorizon}-year cash flow analysis with income, expenses, and net worth projections` }
      : r
    ));
  }, [projectionHorizon]);

  const [filters, setFilters] = useState({
    dateRange: 'last-year',
    categories: ['financial', 'assets'],
    detailLevel: 'standard',
    format: 'pdf',
    includeCharts: true,
    includeRawData: false,
    includeRecommendations: true,
    includeScenarios: false
  });

  const tabs = [
    { id: 'reports', label: 'Report Gallery', icon: 'FileText' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
  ];

  const handleGenerateReport = (report) => {
    setReports(prev => prev.map(r => 
      r.id === report.id 
        ? { ...r, status: 'generating', lastGenerated: 'In Progress' }
        : r
    ));

    // Simulate report generation
    setTimeout(() => {
      setReports(prev => prev.map(r => 
        r.id === report.id 
          ? { ...r, status: 'ready', lastGenerated: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
          : r
      ));
    }, 3000);
  };

  const handlePreviewReport = (report) => {
    setSelectedReport(report);
    setShowPreview(true);
  };

  const handleExportReport = (report) => {
    // Simulate export process
    const link = document.createElement('a');
    link.href = '#';
    link.download = `${report.title.replace(/\s+/g, '_')}.pdf`;
    link.click();
  };

  const handleSaveCustomReport = (newReport) => {
    setReports(prev => [...prev, newReport]);
  };

  const handleResetFilters = () => {
    setFilters({
      dateRange: 'last-year',
      categories: ['financial', 'assets'],
      detailLevel: 'standard',
      format: 'pdf',
      includeCharts: true,
      includeRawData: false,
      includeRecommendations: true,
      includeScenarios: false
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar 
        isExpanded={sidebarExpanded} 
        onToggle={() => setSidebarExpanded(!sidebarExpanded)} 
      />
      
      <div className={`transition-all duration-300 ${sidebarExpanded ? 'ml-64' : 'ml-16'} lg:ml-64`}>
        <Header 
          sidebarExpanded={sidebarExpanded}
          onSidebarToggle={() => setSidebarExpanded(!sidebarExpanded)}
        />
        
        <main className="pt-16 pb-20 lg:pb-8">
          <div className="p-4 sm:p-6 max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">Reports & Analytics</h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Generate comprehensive financial reports and analyze your retirement planning progress
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCustomBuilder(true)}
                  iconName="Plus"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Custom Report</span>
                  <span className="sm:hidden">Custom</span>
                </Button>
                <Button
                  variant="default"
                  iconName="Download"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Export All</span>
                  <span className="sm:hidden">Export</span>
                </Button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-6 bg-muted p-1 rounded-lg w-fit">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200
                    ${activeTab === tab.id
                      ? 'bg-card text-foreground shadow-soft'
                      : 'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            {activeTab === 'reports' ? (
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 sm:gap-6">
                {/* Filters Sidebar */}
                <div className="xl:col-span-1">
                  <ReportFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onReset={handleResetFilters}
                  />
                </div>

                {/* Reports Grid */}
                <div className="xl:col-span-3">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {reports.map((report) => (
                      <ReportCard
                        key={report.id}
                        report={report}
                        onGenerate={handleGenerateReport}
                        onPreview={handlePreviewReport}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <AnalyticsPanel />
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {showPreview && selectedReport && (
        <ReportPreview
          report={selectedReport}
          onClose={() => {
            setShowPreview(false);
            setSelectedReport(null);
          }}
          onExport={handleExportReport}
        />
      )}

      {showCustomBuilder && (
        <CustomReportBuilder
          isOpen={showCustomBuilder}
          onClose={() => setShowCustomBuilder(false)}
          onSave={handleSaveCustomReport}
        />
      )}
    </div>
  );
};

export default ReportsAnalytics;